import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';

const STATUS_META = {
  ok:   { color: 'var(--color-success)', bg: 'var(--color-success-bg)', label: 'Operational', dot: '●' },
  warn: { color: 'var(--color-warning)', bg: 'var(--color-warning-bg)', label: 'Degraded',    dot: '●' },
  fail: { color: 'var(--color-error)',   bg: 'var(--color-error-bg)',   label: 'Down',         dot: '●' },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.warn;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontFamily: 'var(--font-display)', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.06em', fontSize: 11,
      color: m.color, background: m.bg, border: `1px solid ${m.color}`,
      padding: '3px 10px', borderRadius: 999,
    }}>
      <span style={{ fontSize: 9 }}>{m.dot}</span>{m.label}
    </span>
  );
}

function MetaRow({ k, v }) {
  if (v === null || v === undefined || v === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '2px 0' }}>
      <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{k}</span>
      <span style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{String(v)}</span>
    </div>
  );
}

function CheckCard({ check }) {
  const m = STATUS_META[check.status] || STATUS_META.warn;
  return (
    <div style={{
      background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
      borderLeft: `3px solid ${m.color}`, borderRadius: 12, padding: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ fontSize: 15, margin: 0, color: 'var(--text-primary)' }}>{check.label}</h3>
        <StatusPill status={check.status} />
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{check.detail}</p>
      {check.meta && (
        <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: 8 }}>
          {Object.entries(check.meta).map(([k, v]) => <MetaRow key={k} k={k} v={v} />)}
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>
        {check.duration_ms} ms
      </div>
    </div>
  );
}

export function Health() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async (force = false) => {
    force ? setRefreshing(true) : setLoading(true);
    setError('');
    try {
      const res = await api.healthDeep(); // 200 or 503 both carry the payload
      setData(res);
    } catch (err) {
      setError(err.status === 403 ? 'This page is restricted to leadership and admins.' : (err.message || 'Failed to load health'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(() => load(), 30000); // auto-refresh (uses server cache)
    return () => clearInterval(timer.current);
  }, [load]);

  const overall = data && (STATUS_META[data.status] || STATUS_META.warn);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h1 className="text-2xl" style={{ margin: 0, color: 'var(--text-primary)' }}>System Health</h1>
          <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>
            Live dependency &amp; data-pipeline status
            {data?.checked_at && <> · checked {new Date(data.checked_at).toLocaleTimeString()}{data.cached ? ' (cached)' : ''}</>}
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '9px 16px', fontFamily: 'var(--font-display)', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 12,
            color: '#fff', background: 'var(--coros-red)', border: 'none', borderRadius: 8,
            cursor: refreshing ? 'wait' : 'pointer', opacity: refreshing ? 0.6 : 1,
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh Now'}
        </button>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Running checks…</p>}
      {error && (
        <div style={{ padding: 16, borderRadius: 12, background: 'var(--color-error-bg)', border: '1px solid var(--color-error)', color: 'var(--color-error)' }}>
          {error}
        </div>
      )}

      {data && !error && (
        <>
          {/* Overall banner */}
          <div className="accent-cut" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            background: overall.bg, border: `1px solid ${overall.color}`, borderRadius: 14,
            padding: '18px 22px', marginBottom: 24,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ fontSize: 26, color: overall.color, lineHeight: 1 }}>●</span>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, textTransform: 'uppercase', fontSize: 22, color: overall.color, lineHeight: 1 }}>
                  {data.status === 'ok' ? 'All Systems Operational' : data.status === 'warn' ? 'Degraded Performance' : 'Service Disruption'}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
                  {data.summary.ok} operational · {data.summary.warn} degraded · {data.summary.fail} down
                </div>
              </div>
            </div>
          </div>

          {/* Check cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
            {data.checks.map((c) => <CheckCard key={c.name} check={c} />)}
          </div>
        </>
      )}
    </div>
  );
}

export default Health;
